import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@tsin-env/environment';
import { Observable } from 'rxjs';

const BASE = `${environment.apiUrl2}/email-tracking`;

/** Outcome of a bulk dispatch as recorded by the tracker. The union lists the
 *  values the UI styles explicitly; any other server value still renders with a
 *  neutral badge.
 *  Résultat d'un envoi groupé tel qu'enregistré par le suivi. */
export type EmailDispatchStatus = 'SUCCESS' | 'PARTIAL' | 'FAILED' | 'PENDING' | (string & {});

/** Application phase a dispatch belongs to / Phase de candidature de l'envoi */
export type EmailDispatchPhase = 'PHASE_1' | 'PHASE_2' | (string & {});

/** Per-template breakdown inside a single dispatch — one entry per recipient
 *  group (e.g. ELIGIBLE, INELIGIBLE, TDM_REFERRAL).
 *  Ventilation par gabarit à l'intérieur d'un envoi — une entrée par groupe. */
export interface EmailDispatchGroup {
  group: string;
  templateKey: string;
  recipientCount: number;
}

/** One row of the dispatch report — a single bulk send.
 *  Une ligne du rapport d'envois — un envoi groupé. */
export interface EmailDispatchLog {
  _id: string;
  emailType: string;
  category: string;
  phase: EmailDispatchPhase;
  status: EmailDispatchStatus;
  portalDrawId?: string;
  /** Emails sent / Courriels envoyés */
  recipientCount: number;
  /** Distinct people — a referral can hit two templates.
   *  Personnes distinctes — une référence peut toucher deux gabarits. */
  uniqueRecipientCount: number;
  /** Optional because a dispatch that failed before fan-out has no breakdown.
   *  Optionnel : un envoi échoué avant la ventilation n'en possède pas. */
  groups?: EmailDispatchGroup[];
  triggeredByUserId?: string;
  triggeredByName?: string;
  triggeredByEmail?: string;
  triggeredByRole?: string;
  durationMs?: number;
  sentAt: string;
}

/** A single addressee within a dispatch, returned only by the detail endpoint.
 *  Un destinataire d'un envoi, retourné uniquement par l'endpoint de détail. */
export interface EmailDispatchRecipient {
  email: string;
  name?: string;
  applicationNo?: string;
  applicationId?: string;
  decision?: string;
  group?: string;
  templateKey?: string;
}

/** Detail payload — the dispatch document plus its recipients.
 *  Charge utile de détail — le document d'envoi et ses destinataires. */
export interface EmailDispatchLogDetail extends EmailDispatchLog {
  recipients: EmailDispatchRecipient[];
}

/** Server pagination envelope. `total` is capped at `windowLimit`, so it is the
 *  number of rows actually reachable by paging; `totalMatching` is the real,
 *  uncapped count of rows matching the filter.
 *  Enveloppe de pagination du serveur. */
export interface EmailDispatchPagination {
  page: number;
  limit: number;
  /** Rows reachable by paging — capped at the window / Lignes accessibles */
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  /** Hard cap the server applies to `total` / Plafond appliqué par le serveur */
  windowLimit: number;
  /** Real count matching the filter, uncapped / Nombre réel, sans plafond */
  totalMatching: number;
}

export interface EmailDispatchLogListResponse {
  data: EmailDispatchLog[];
  pagination: EmailDispatchPagination;
}

/** Query parameters accepted by the dispatch-log report. Dates are ISO-8601
 *  strings; undefined keys are omitted from the request.
 *  Paramètres de requête du rapport d'envois. */
export interface EmailDispatchLogFilter {
  category?: string;
  phase?: string;
  status?: string;
  /** Inclusive lower bound, ISO-8601 / Borne inférieure incluse */
  startDate?: string;
  /** Inclusive upper bound, ISO-8601 / Borne supérieure incluse */
  endDate?: string;
  page?: number;
  limit?: number;
}

/** Email dispatch tracking HTTP service.
 *  Service HTTP de suivi des envois de courriels. */
@Injectable({ providedIn: 'root' })
export class EmailTrackingHttpService {
  private readonly http = inject(HttpClient);

  /** Fetch the paginated dispatch report.
   *  Récupérer le rapport d'envois paginé. */
  getDispatchLogs(filter: EmailDispatchLogFilter = {}): Observable<EmailDispatchLogListResponse> {
    let params = new HttpParams();
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<EmailDispatchLogListResponse>(`${BASE}/dispatch-logs`, { params });
  }

  /** Fetch one dispatch with its recipient list. Unknown IDs return 404.
   *  Récupérer un envoi et sa liste de destinataires. */
  getDispatchLog(id: string): Observable<EmailDispatchLogDetail> {
    return this.http.get<EmailDispatchLogDetail>(`${BASE}/dispatch-logs/${id}`);
  }
}
